import React, { useContext, useEffect, useState } from 'react';
import { client, consoleLogInDev } from 'shared';
import { PostModel } from './NewsfeedModels';
import { calculatePostAdded } from './dateUtils';
import { isLoggedIn } from '../../../api/UserApi';

interface NewsfeedContextType {
    content: PostModel[];
    refreshing: boolean;
    refreshContent: (groupId?: number) => void;
    loadMore: () => void;
    refreshPost: (post: PostModel) => void;
    groupId?: number;
}

export const NewsfeedContext = React.createContext<NewsfeedContextType>({
    content: [],
    refreshing: false,
    refreshContent: (groupId?: number) => {},
    loadMore: () => {},
    refreshPost: () => {},
});

export function NewsfeedContextProvider({ children, groupId }: { children: React.ReactNode; groupId?: number }) {
    const [content, setContent] = useState<PostModel[]>([]);
    const [refreshing, setRefreshing] = useState(false);
    const fetchUrl = groupId ? `api/post/?group=${groupId}` : 'api/post/';

    const getContent = () => {
        if (!isLoggedIn()) {
            return;
        }

        setRefreshing(true);
        client(fetchUrl)
            .then((posts: PostModel[]) => {
                posts.forEach(
                    (contentItem: PostModel) => (contentItem['postAge'] = calculatePostAdded(contentItem.added_at)),
                );
                setContent(posts);
            })
            .catch(consoleLogInDev)
            .finally(() => setRefreshing(false));
    };

    useEffect(getContent, []);

    const loadMore = () => {
        let urlStart = groupId ? `${fetchUrl}&` : `${fetchUrl}?`;
        client(`${urlStart}index=${content[content.length - 1].id}`)
            .then((posts) => {
                posts.forEach(
                    (contentItem: PostModel) => (contentItem['postAge'] = calculatePostAdded(contentItem.added_at)),
                );
                if (posts.length > 0) {
                    setContent(content.concat(posts));
                }
            })
            .catch(consoleLogInDev);
    };

    const refreshPost = (post: PostModel) => {
        client(`api/post/${post.id}/`)
            .then((refreshedPost: PostModel) => {
                refreshedPost['postAge'] = calculatePostAdded(refreshedPost.added_at);
                let updatedContent = content.map((existingPost) =>
                    existingPost.id === post.id ? refreshedPost : existingPost,
                );
                setContent(updatedContent);
            })
            .catch(consoleLogInDev);
    };

    const contextValue = {
        content,
        refreshing,
        refreshContent: getContent,
        loadMore,
        refreshPost,
        groupId,
    };
    return <NewsfeedContext.Provider value={contextValue}>{children}</NewsfeedContext.Provider>;
}

export const useNewsfeedContext = () => {
    return useContext(NewsfeedContext);
};
